import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { MOBILE_TOPBAR_HEIGHT } from "../../contexts/SidebarContext";
import { BOTTOM_TAB_BAR_HEIGHT, BOTTOM_TAB_BAR_SAFE_AREA } from "./BottomTabBar";
import { hapticSelection } from "../../lib/haptics";

/**
 * Shared building blocks for the native app's landing screens (Insiders,
 * Charts, Compare, Earnings): a viewport-centred container that exactly fills
 * the space between the top bar and the tab bar, a compact left-aligned
 * header (no logo — the top bar carries the brand), an uppercase section
 * label, and dense tappable rows in the iOS list idiom.
 */

type NativeLandingContainerProps = {
  children: React.ReactNode;
  /**
   * Fill and vertically centre the space between the app bars. Off when the
   * block sits under other content, where claiming the full viewport would
   * push it off the bottom of the screen.
   */
  fillViewport?: boolean;
};

export const NativeLandingContainer = ({
  children,
  fillViewport = true,
}: NativeLandingContainerProps) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff",
        px: 2,
        py: 2,
        // Fill exactly the space between the top bar and the tab bar and
        // centre the whole block vertically
        minHeight: fillViewport
          ? `calc(100dvh - ${MOBILE_TOPBAR_HEIGHT + BOTTOM_TAB_BAR_HEIGHT}px - env(safe-area-inset-top, 0px) - ${BOTTOM_TAB_BAR_SAFE_AREA})`
          : "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: fillViewport ? "center" : "flex-start",
      }}
    >
      {children}
    </Box>
  );
};

type NativeLandingHeaderProps = {
  title: string;
  subtitle: string;
};

export const NativeLandingHeader = ({ title, subtitle }: NativeLandingHeaderProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "1.5rem",
          letterSpacing: "-0.02em",
          color: isDark ? "#ffffff" : "#1d1d1f",
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.875rem",
          color: isDark ? "#9ca3af" : "#64748b",
          mb: 2.5,
        }}
      >
        {subtitle}
      </Typography>
    </>
  );
};

type NativeSectionLabelProps = {
  children: React.ReactNode;
};

export const NativeSectionLabel = ({ children }: NativeSectionLabelProps) => {
  const theme = useTheme();
  return (
    <Typography
      sx={{
        mt: 3.5,
        mb: 1,
        fontSize: "0.6875rem",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: theme.palette.mode === "dark" ? "#9ca3af" : "#64748b",
      }}
    >
      {children}
    </Typography>
  );
};

type NativeIconChipProps = {
  color: string;
  children: React.ReactNode;
};

/** 40px tinted icon chip for rows whose leading visual is an accent icon. */
export const NativeIconChip = ({ color, children }: NativeIconChipProps) => (
  <Box
    sx={{
      width: 40,
      height: 40,
      borderRadius: 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color,
      backgroundColor: `${color}1A`,
    }}
  >
    {children}
  </Box>
);

type NativeListRowProps = {
  leading: React.ReactNode;
  title: string;
  subtitle?: string;
  onClick?: () => void;
  /** Row is gated: dimmed, not tappable, and shows a "Pro" pill (hover
   * tooltips never fire on touch, so the pill carries that message). */
  disabled?: boolean;
};

export const NativeListRow = ({ leading, title, subtitle, onClick, disabled }: NativeListRowProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Box
      onClick={() => {
        if (disabled || !onClick) return;
        hapticSelection();
        onClick();
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.75,
        p: 1.5,
        borderRadius: 3,
        opacity: disabled ? 0.55 : 1,
        backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
        border: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.1)",
        WebkitTapHighlightColor: "transparent",
        transition: "transform 0.1s ease, background-color 0.1s ease",
        "&:active": {
          backgroundColor: disabled
            ? undefined
            : isDark
              ? "rgba(255, 255, 255, 0.08)"
              : "rgba(0, 0, 0, 0.06)",
          transform: disabled ? undefined : "scale(0.98)",
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {leading}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{
            fontWeight: 600,
            fontSize: "0.9375rem",
            color: isDark ? "#ffffff" : "#1d1d1f",
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            noWrap
            sx={{
              fontSize: "0.8125rem",
              color: isDark ? "#9ca3af" : "#64748b",
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {disabled && (
        <Typography
          sx={{
            flexShrink: 0,
            fontSize: "0.6875rem",
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: "#22c55e",
            backgroundColor: "rgba(34, 197, 94, 0.1)",
            px: 1,
            py: 0.25,
            borderRadius: 1,
          }}
        >
          Pro
        </Typography>
      )}
      {!disabled && (
        <ChevronRightIcon
          sx={{
            flexShrink: 0,
            fontSize: 20,
            color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
          }}
        />
      )}
    </Box>
  );
};
